/**
 * StoreHistory Middleware
 * 
 * This uses the CreateStore.js middleware to hook into all dispatch events and target any configured stores 
 * to allows users to undo and redo changes to those stores.
 * 
 * Current max history recorded is 10, this can be modified with MAX_HISTORY_COUNT
 * startTrackingStoreChanges adds two keyboard eventListeners ctrl+z = undo ctrl+shift+z = redo
 * 
 * SETUP
 * 
 * Place file into src/middleware/stores
 * Add default export to utils/CreateStore.js middleware loader if not present at the front.
 * @example let _middlewares = [StoreHistory,Storage, StoreLogger, ChangeStateMiddleware];
 * 
 * Somewhere in your project configure the middleware to track what stores you want to monitor for changes.
 * make sure this file is imported into your index.js or app.js or other starting files.
 * @example inside src/config/Stores.js add
 * import { trackStoreHistory } from "@@/middleware/stores/StoreHistory";
 * trackStoreHistory([{store:RecordStore}])
 * 
 * By default any and all changes to a store will be tracked, however it is best to only track changes 
 * the user will see. In order to limit what changes to a store will trigger this middleware you can 
 * optionally specify a property on said store that will trigger the tracking event.
 * @example trackStoreHistory([{store:RecordStore,trackedPropertyName:"response"}])
 * 
 * 
 * 
 * USAGE IN APP
 * 
 * Now you can start/stop monitoring for changes and reset to the initial state after 
 * startTrackingStoreChanges() is invoked.
 * 
 * @function startTrackingStoreChanges(string storeName)
 * This will start tracking changes on the "Store",and the current state as initial history and
 * add keyboard eventListeners for undo = ctrl+z and redo ctrl+shift+z
 * @example startTrackingStoreChanges("RecordStore")
 * 
 * @function stopTrackingStoreChanges()
 * This will stop tracking changes on the store passed to startTrackingStoreChanges and 
 * remove all keyboard eventListeners
 * @example stopTrackingStoreChanges()
 * 
 * @function resetStoreHistory()
 * This will reset the state of the store to when startTrackingStoreChanges() was invoked.
 * @example resetStoreHistory()
 * 
 * @function getStoreHistoryPastLength()
 * This will return the current number of undo's available
 * @example getStoreHistoryPastLength()
 * 
 * @function getStoreHistoryFutureLength();
 * This will return the current numbe of redo's available
 * @example getStoreHistoryFutureLength()
 * 
 * 
 * NOTES
 * 
 * Make sure you stopTrackingStoreChanges() when you are not expecting the user to be changing 
 * state in that store. This function also removes the keyboard shortcut eventListeners for undo and redo
 * 
 * This middleware requires a small addition to the utils/CreateStore.js factory
 * If you encounter the error 'store.addEvent method required' please add the following onto the 
 * Store object inside utils/CreateStore.js
 * @example 
 *  Store.addEvent(eventName,func){
        events[eventName] = func ;
    }
 */

import { dispatch } from "../src/Dispatcher";
const bodyCSS = "color:gray;font-weight:lighter";
const prevCSS = "color:gray;font-weight:bold";
const actionCSS = "color:#87A7DB;font-weight:lighter";
const storeCSS = "font-weight:bold";
const nextCSS = "color:#6bc679;font-weight:bold";
const stores = {};
const MAX_HISTORY_COUNT = 10;
const RESTORE_INITIAL_HISTORY = "RESTORE_INITIAL_HISTORY";
const REDO = "REDO";
const UNDO = "UNDO";
const hasOwn = Object.prototype.hasOwnProperty;
let _listenersAdded = false;
let _storeName;
export const resetStoreHistory = () => {
  dispatch(RESTORE_INITIAL_HISTORY);
};
export const startTrackingStoreChanges = storeName => {
  if (!storeName) {
    console.error("startTrackingStoreChanges:Missing Parameter - Store name required");
    return;
  }
  if (_listenersAdded) return;
  document.addEventListener("keydown", undoListener, false);
  document.addEventListener("keydown", redoListener, false);
  _storeName = storeName;
  setInitialStateForStore(storeName);
  _listenersAdded = true;
};
export const stopTrackingStoreChanges = () => {
  document.removeEventListener("keydown", undoListener);
  document.removeEventListener("keydown", redoListener);
  const history = stores[_storeName];
  if (!history) return;
  history.past = [];
  history.future = [];
  history.present = null;
  logHistoryStopTracking(history);
  _listenersAdded = false;
  _storeName = null;
};
export const trackStoreHistory = arrayOfTrackedStores => {
  arrayOfTrackedStores.forEach(history => {
    const store = history.store;
    const trackedPropertyName = history.trackedPropertyName;
    const match = store.hasOwnProperty(store.getName());
    if (!match) addStore(store, trackedPropertyName);
  });
};
export const getStoreHistoryPastLength = () => {
  if (!_storeName) return 0;
  return stores[_storeName].past.length;
};
export const getStoreHistoryFutureLength = () => {
  if (!_storeName) return 0;
  return stores[_storeName].future.length;
};
export const storeHistoryMiddleware = name => events => action => state => next => {
  if (stores.hasOwnProperty(name) && _storeName === name) {
    const history = stores[name];
    if (action.actionType === RESTORE_INITIAL_HISTORY) {
      history.present = history.initial;
      history.past = [];
      history.future = [];
      logHistoryReset(history);
    } else {
      switch (action.actionType) {
        case UNDO:
          if (history.past.length === 0) {
            logHistoryNoMorePast(history);
            break;
          }
          ;
          logHistoryUndo(history);
          const oldPresent = history.present;
          const previous = history.past[history.past.length - 1];
          const newPast = history.past.slice(0, history.past.length - 1);
          history.past = newPast;
          history.present = previous;
          history.future.unshift(oldPresent);
          break;
        case REDO:
          if (history.future.length === 0) {
            logHistoryNoMoreFuture(history);
            break;
          }
          ;
          logHistoryRedo(history);
          const nextFuture = history.future[0];
          const newFuture = history.future.slice(1);
          history.past.push(history.present);
          history.present = nextFuture;
          history.future = newFuture;
          break;
        default:
          const nextState = next(name, events, action, state);
          if (!hasChanged(history, nextState)) return nextState;
          history.past.push(history.present);
          history.present = nextState;
          history.future = [];
          logHistoryChange(history);
          break;
      }
    }
    if (history.past.length > MAX_HISTORY_COUNT) {
      history.past.shift();
    }
    if (history.future.length > MAX_HISTORY_COUNT) {
      history.future.pop();
    }
    return history.present;
  }
  return next(name, events, action, state);
};
function addStore(store, trackedPropertyName) {
  const state = store.getState();
  const name = store.getName();
  if (!store.addEvent) {
    console.error("StoreHistory middleware: store.addEvent method required. " + "Please view StoreHistory.js for details");
    return;
  }
  store.addEvent(UNDO, (action, state) => {
    return state;
  });
  store.addEvent(REDO, (action, state) => {
    return state;
  });
  store.addEvent(RESTORE_INITIAL_HISTORY, (action, state) => {
    return state;
  });
  stores[name] = {
    trackedPropertyName: trackedPropertyName,
    name: store.getName(),
    store: store,
    initial: state,
    past: [],
    present: state,
    future: []
  };
}
function undoListener(e) {
  if (e.ctrlKey && !e.shiftKey && String.fromCharCode(e.keyCode).toLowerCase() === "z") {
    e.preventDefault();
    e.stopPropagation();
    dispatch(UNDO, _storeName);
    // Do some stuff...
  }
}
function redoListener(e) {
  if (e.ctrlKey && e.shiftKey && String.fromCharCode(e.keyCode).toLowerCase() === "z") {
    e.preventDefault();
    e.stopPropagation();
    dispatch(REDO, _storeName);
    // Do some stuff...
  }
}
function setInitialStateForStore(storeName) {
  if (!stores[storeName]) return;
  stores[storeName].initial = stores[storeName].store.getState();
  stores[storeName].present = stores[storeName].initial;

  //console.log(`StoreHistoryMiddleware: Tracking ${_storeName} changes`,stores[storeName]);
  logHistoryTracking(stores[storeName]);
}
function is(x, y) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    // eslint-disable-next-line no-self-compare
    return x !== x && y !== y;
  }
}
function shallowEqual(objA, objB) {
  if (is(objA, objB)) return true;
  if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (let i = 0; i < keysA.length; i++) {
    if (!hasOwn.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}
function hasChanged(history, state) {
  if (history.trackedPropertyName) {
    const trackedProp = history.trackedPropertyName;
    const equal = shallowEqual(history.present[trackedProp], state[trackedProp]);
    return !equal;
  }
  return !shallowEqual(history.present, state);
}
function logHistoryChange(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%chistory %c${_storeName} %c(${history.past.length} ${trackedProps} changes)`, bodyCSS, storeCSS, bodyCSS);
  console.log(`%cpast`, prevCSS, history.past);
  console.log(`%cpresent`, nextCSS, history.present);
  console.groupEnd();
}
function logHistoryReset(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%crestoring history %c${_storeName} %c(reverting all ${trackedProps} changes)`, bodyCSS, storeCSS, bodyCSS);
  console.log("%chistory", actionCSS, history);
  console.groupEnd();
}
function logHistoryUndo(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%chistory %c${_storeName} %c(reverting 1 ${trackedProps} change)`, bodyCSS, storeCSS, bodyCSS);
  console.log(`%cpast`, prevCSS, history.past);
  console.log(`%cpresent`, nextCSS, history.present);
  console.log(`%cfuture`, nextCSS, history.future);
  console.groupEnd();
}
function logHistoryRedo(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%chistory %c${_storeName} %c(re-applying 1 ${trackedProps} change)`, bodyCSS, storeCSS, bodyCSS);
  console.log(`%cpast`, prevCSS, history.past);
  console.log(`%cpresent`, nextCSS, history.present);
  console.log(`%cfuture`, nextCSS, history.future);
  console.groupEnd();
}
function logHistoryTracking(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "all";
  console.groupCollapsed(`%cStarted Monitoring %c${_storeName} %c(for ${trackedProps} changes)`, actionCSS, storeCSS, bodyCSS);
  console.log("%chistory", actionCSS, history);
  console.groupEnd();
}
function logHistoryStopTracking(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "all";
  console.groupCollapsed(`%cStopped Monitoring %c${_storeName} %c(for ${trackedProps} changes)`, actionCSS, storeCSS, bodyCSS);
  console.log("%chistory", actionCSS, history);
  console.groupEnd();
}
function logHistoryNoMorePast(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%chistory %c${_storeName} %c(no more ${trackedProps} changes to revert)`, bodyCSS, storeCSS, bodyCSS);
  console.log(`%cpast`, prevCSS, history.past);
  console.log(`%cpresent`, nextCSS, history.present);
  console.groupEnd();
}
function logHistoryNoMoreFuture(history) {
  const trackedProps = history.trackedPropertyName ? `state.${history.trackedPropertyName}` : "";
  console.groupCollapsed(`%chistory %c${_storeName} %c(no more ${trackedProps} changes to re-apply)`, bodyCSS, storeCSS, bodyCSS);
  console.log(`%cpresent`, nextCSS, history.present);
  console.log(`%cfuture`, nextCSS, history.future);
  console.groupEnd();
}